import { useUserStore } from '@/store'
import { ClearCache } from '@/utils'
import { NavigationJump, UtilShowToast } from '@/utils/uniVab'
import CryptoJS from 'crypto-js'
import md5 from 'md5'
import qs from 'qs'

export type TCustomHeaders = {
  token?: string
  'Content-Type'?: string
  signal?: string
}

export interface IHttpResult<T> {
  code: string
  context: T
  message: string
  requestId: string
  success: boolean
}

export function getQuestTokenCode() {
  return ['0x00000110', '0x00000111', '0x00000112', '0x00000113', '0x00000109']
}
// 获取随机数
export function getNonce(): string {
  const chars = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
  ]
  let res = ''
  for (let i = 0; i < 6; i++) {
    const id = Math.ceil(Math.random() * 35)
    res += chars[id]
  }
  return res
}

// 对字符串格式的参数进行排序
export function paramSort(params: string) {
  const arr: string[] | any = []
  params.split('&').forEach((e) => {
    const index = e.indexOf('=')
    if (index === -1) {
      arr.push([e, ''])
    } else {
      const value = e.substring(index + 1, e.length)
      arr.push([e.substring(0, index), value])
    }
  })
  arr.sort()
  params = ''
  arr.forEach((e: string | any) => {
    if (e[1] != null && e[1] !== '') {
      params += `&${e[0]}=${e[1]}`
    }
  })
  if (params.length > 0) {
    params = params.substring(1)
  }

  return params
}

// 对象合并
export function extend(des: any, src: any, override: any) {
  if (Array.isArray(src)) {
    for (let i = 0, len = src.length; i < len; i++) {
      extend(des, src[i], override)
    }
  }
  for (const i in src) {
    if (override || !(i in des)) {
      des[i] = src[i]
    }
  }
  return des
}

// 加解密
export function encrypt(eKey: any, siv: any, eData: any) {
  const key = CryptoJS.enc.Utf8.parse(eKey)
  const iv = CryptoJS.enc.Utf8.parse(siv)
  const data = CryptoJS.enc.Utf8.parse(eData)
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  return encrypted.toString()
}

// 参数排序
export function jsonSort(params: any) {
  let txt = ''
  if (Object.prototype.toString.call(params) === '[object Object]') {
    const arr = []
    for (const key in params) {
      const val = params[key]
      if (
        Object.prototype.toString.call(val) === '[object Array]' ||
        Object.prototype.toString.call(val) === '[object Object]'
      ) {
        continue
      }
      // 有效值才参与签名
      if (
        val !== undefined &&
        val != null &&
        val !== '' &&
        JSON.stringify(val) !== '{}' &&
        JSON.stringify(val) !== '[]'
      ) {
        arr.push(`${key}=${val}`)
      }
    }
    // 字典升序
    arr.sort()
    txt = arr.join('&')
  }
  return txt
}

// 请求拦截配置
export async function getRequestConfig(config: any) {
  const appId = `${import.meta.env.VITE_APP_ID}`
  const appKey = `${import.meta.env.VITE_APP_KEY}`
  const timestamp = Date.now()
  const nonce = getNonce()
  const userStore = useUserStore()
  const { token } = userStore.userInfo
  const getTokenStr = token

  let signStr = ''
  let requestParams
  let sortedParams = ''
  let arr
  let jsonBody = true
  // 获取平台信息
  let headers: any = {
    'Content-Type': 'application/json;charset=utf-8',
    appId,
    token: getTokenStr,
    timestamp,
    nonce,
    sysCode: 'h5',
  }

  // 对get、delete 类型的请求，获取排序后的参数
  if (config.method! in { get: true, delete: true }) {
    requestParams = config.params
    // 为了兼容不规范写法，获取url上的参数进行处理，正常应该不会放在url上，而是使用axios的方式
    const urlParams: { [k: string]: any } = {}
    arr = config.url!.split('?')
    if (arr && arr.length > 1) {
      const pArr = arr[1].split('&')
      if (pArr) {
        for (const i of pArr) {
          const p = i.split('=')
          if (p.length > 1 && p[1]) {
            urlParams[p[0]] = p[1]
          }
        }
      }
    }

    // 用config里参数覆盖url上的参数
    if (requestParams && typeof requestParams === 'object') {
      sortedParams = paramSort(qs.stringify(requestParams, { arrayFormat: 'comma', encode: false }))
    } else {
      sortedParams = paramSort(qs.stringify(urlParams, { arrayFormat: 'comma', encode: false }))
    }

    // 对post、put 类型的请求，获取排序后的参数
  } else if (config.method! in { post: true, put: true }) {
    requestParams = config.data || {}

    if (typeof requestParams === 'string') {
      headers = extend(
        headers,
        { 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' },
        true,
      )
      jsonBody = false
    }

    if (jsonBody) {
      if (requestParams.data) {
        requestParams.data = encrypt(
          appKey,
          `${Math.floor(timestamp / 1000)}${nonce}`,
          requestParams.data,
        )
        config.data = requestParams
      }
      sortedParams = jsonSort(requestParams)
    } else {
      sortedParams = paramSort(qs.stringify(requestParams, { arrayFormat: 'comma', encode: false }))
    }
  }

  signStr = `${(sortedParams ? `${sortedParams}&` : '') + appKey}&${timestamp}&${nonce}`
  const sign = md5(signStr)
  headers = extend(headers, { sign }, true)
  if (getTokenStr) {
    headers = extend(headers, { token: getTokenStr }, true)
  }

  // 可自定义headers
  if (config.headers) {
    headers = extend(headers, config.headers, true)
  }
  config.headers = headers
  return config
}

// 登录失效
export function loginFailure() {
  UtilShowToast({
    title: '会话登录已失效',
    success: function () {
      // 清除商家信息  并跳转
      ClearCache()
      NavigationJump('reLaunch', {
        url: `/pages-sub/login/index?redirect=/pages/home/index`,
      })
    },
  })
}

// 请求参数配置
export function getParamsConfig(url: string, method: string, params: any, headers: TCustomHeaders) {
  const config: any = {
    url,
    method,
  }
  if (!params) {
    params = {}
  }
  if (method === 'get') {
    // 解决get方式传对象的问题
    Object.keys(params).forEach((key) => {
      if (typeof params[key] === 'object' && params[key] !== null) {
        params[key] = params[key].join(',')
      }
    })

    config.paramsSerializer = {
      serialize: (params: any) => qs.stringify(params, { arrayFormat: 'repeat' }),
    }
  }
  if (headers) {
    config.headers = headers
    // 请求标识, 部分情况需要取消对应的请求
    headers.signal && (config.signal = headers.signal)
  }
  if (method in { get: true, delete: true }) {
    config.params = params
  } else {
    config.data = params
  }

  return config
}
